import React, { useContext } from 'react'
import { Switch, Skeleton, Alert } from 'antd'
import { Horizontal, Vertical, Texto, GraviGrid, BBDTag } from '@gravitate-js/excalibrr'
import { useLaunchpad } from './components/context/LaunchpadContext'
import { useSharedHome } from '../context/SharedHomeContext'
import { SecurityContext } from '../../../Navigation/SecurityContext'
import { BackgroundRefreshSpinner } from '../components/BackgroundRefreshSpinner'
import { RecentActivityItem } from './components/ui/RecentActivityItem'
import { Bar, BarChart, ResponsiveContainer, Tooltip } from 'recharts'
import { supplierColumnDefs } from './components/columnDefs/SupplierColumnDefs'
import { invoiceColumnDefs } from './components/columnDefs/InvoiceColumnDefs'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { TerminalDetailRenderer } from './components/renderers/TerminalDetailRenderer'
import { DeltaDisplay } from './components/ui/DeltaDisplay'
import { useSupplierMetrics } from './components/hooks/useSupplierMetrics'
import { useOldestInvoices } from './components/hooks/useOldestInvoices'
import { transformRecentActivity } from './components/utils/transformRecentActivity'
import { generateNavigationUrlForMatch } from './components/utils/navigationUtils'
import { gridViewManager } from '../../../../utils/grid'
import { useUserSettings } from '@hooks/settings'
import { useSupplierReconciliation } from '../../../../api/useSupplierReconciliation'
import { StatusAggregation } from '../components/StatusAggregation'
import { formatMetricNumber, formatDollarMetric } from './components/utils/formatMetricNumber'
import { handleNavigation, handleGridRowClick } from '../utils/navigationHelpers'

export function Launchpad() {
  return <LaunchpadContent />
}

const OLDEST_INVOICES_STORAGE_KEY = 'launchpad-oldest-invoices-grid'

function LaunchpadContent() {
  // Get shared data from context
  const {
    data: { matches, isLoading, isBackgroundRefreshing, trends: trendsData, hasError, error },
    assignedToMe,
    setAssignedToMe,
    dateRange,
  } = useSharedHome()
  const { user } = useContext(SecurityContext)

  function createMatchViewUrl(baseUrl: string, includeAssigned = true) {
    const params = new URLSearchParams()
    params.append('date_range[]', dateRange.start.toISOString())
    params.append('date_range[]', dateRange.end.toISOString())

    if (includeAssigned) {
      params.append('assigned_to_me', assignedToMe ? 'true' : 'false')
    }

    return `${baseUrl}?${params.toString()}`
  }

  // Derive metrics and trends from shared data
  const {
    metrics: calculatedMetrics,
    trends: calculatedTrends,
    matchCounts,
    supplierBreakdowns,
  } = useLaunchpad(matches)

  // Use real metrics from latest snapshot if available
  const metrics = React.useMemo(() => {
    if (trendsData && trendsData.length > 0) {
      // Sort trends by date to ensure proper oldest/newest identification
      const sortedTrends = [...trendsData].sort(
        (a, b) => new Date(a.date || '').getTime() - new Date(b.date || '').getTime()
      )

      const latestSnapshot = sortedTrends[sortedTrends.length - 1]
      return {
        autoApprovePercentage: latestSnapshot.auto_match_percentage,
        totalPastDue: latestSnapshot.total_past_due,
        totalOpenItems: latestSnapshot.total_open_matches,
        totalUnmatchedItems: latestSnapshot.total_orphan_bols,
      }
    }

    // Fallback to calculated metrics
    return calculatedMetrics
  }, [trendsData, calculatedMetrics])

  // Use real trends data from API if available, otherwise use calculated trends
  const { trends, hasValidTrendData } = React.useMemo(() => {
    if (trendsData && trendsData.length > 1) {
      // Sort trends by date to ensure proper oldest/newest identification
      const sortedTrends = [...trendsData].sort(
        (a, b) => new Date(a.date || '').getTime() - new Date(b.date || '').getTime()
      )

      // Get the oldest and latest snapshots from sorted data
      const oldestSnapshot = sortedTrends[0]
      const latestSnapshot = sortedTrends[sortedTrends.length - 1]

      // Calculate percentage changes with trend capping
      const calculateTrend = (oldValue: number, newValue: number): number => {
        if (oldValue === 0) return newValue > 0 ? 100 : 0

        const trend = ((newValue - oldValue) / oldValue) * 100

        // Cap trends at -100% to 100% for display purposes
        if (trend > 100) return 100
        if (trend < -100) return -100

        return trend
      }

      // Check if we have valid data (not all zeros)
      const hasData =
        latestSnapshot.total_past_due > 0 ||
        latestSnapshot.total_open_matches > 0 ||
        latestSnapshot.total_orphan_bols > 0

      return {
        trends: {
          autoApprovePercentage: calculateTrend(
            oldestSnapshot.auto_match_percentage,
            latestSnapshot.auto_match_percentage
          ),
          totalPastDue: calculateTrend(oldestSnapshot.total_past_due, latestSnapshot.total_past_due),
          totalOpenItems: calculateTrend(oldestSnapshot.total_open_matches, latestSnapshot.total_open_matches),
          totalUnmatchedItems: calculateTrend(oldestSnapshot.total_orphan_bols, latestSnapshot.total_orphan_bols),
        },
        hasValidTrendData: hasData && trendsData.length > 1,
      }
    }

    // Fallback to calculated trends
    return { trends: calculatedTrends, hasValidTrendData: false }
  }, [trendsData, calculatedTrends])
  const navigate = useNavigate()
  const { updateSetting } = useUserSettings()

  // Centralized sparkData with consistent date handling and proper sorting
  const sparkData = React.useMemo(() => {
    if (trendsData && trendsData.length > 0) {
      // Sort trends by date for consistent chart data
      const sortedTrends = [...trendsData].sort(
        (a, b) => new Date(a.date || '').getTime() - new Date(b.date || '').getTime()
      )

      return sortedTrends.map((snapshot) => ({
        // Include all metrics in centralized sparkData
        past_due: snapshot.total_past_due,
        open_items: snapshot.total_open_matches,
        auto_approve: snapshot.auto_match_percentage * 100, // Convert to percentage
        orphan_bols: snapshot.total_orphan_bols,
        date: (() => {
          // Handle mixed UTC/non-UTC date formats
          const dateStr = snapshot.date
          if (!dateStr) return 'Unknown'

          let result
          if (dateStr.endsWith('Z')) {
            // UTC date - parse as UTC
            result = dayjs.utc(dateStr).format('MMM D, YYYY')
          } else {
            // Non-UTC date - parse as is (don't convert to UTC)
            result = dayjs(dateStr).format('MMM D, YYYY')
          }

          return result
        })(),
      }))
    }
    return []
  }, [trendsData])

  // Individual sparkData arrays using centralized data
  const autoApproveSparkData = React.useMemo(
    () => sparkData.map((item) => ({ value: item.auto_approve, date: item.date })),
    [sparkData]
  )

  const pastDueSparkData = React.useMemo(
    () => sparkData.map((item) => ({ value: item.past_due, date: item.date })),
    [sparkData]
  )

  const openItemsSparkData = React.useMemo(
    () => sparkData.map((item) => ({ value: item.open_items, date: item.date })),
    [sparkData]
  )

  const unmatchedSparkData = React.useMemo(
    () => sparkData.map((item) => ({ value: item.orphan_bols, date: item.date })),
    [sparkData]
  )

  // Get API hooks
  const { useRecentActivity } = useSupplierReconciliation()

  // Real data from matches - SUPPLIER OVERVIEW FIXED
  const supplierMetrics = useSupplierMetrics(matches)
  // Real data from matches - TOP 10 OLDEST INVOICES IMPLEMENTED
  const top10OldestInvoices = useOldestInvoices(matches)

  // Use the same date range as the shared context for consistency
  const recentActivityDateRange = React.useMemo(
    () => ({
      start: dateRange.start,
      end: dateRange.end,
    }),
    [dateRange.start, dateRange.end]
  )

  // Real data from API - RECENT ACTIVITY FROM API
  const { data: recentActivityAPIData, isLoading: isRecentActivityLoading } = useRecentActivity({
    dateRange: recentActivityDateRange,
    assignedToMe,
    limit: 20,
  })

  // Transform API data to UI format and filter by assignee if needed
  const recentActivityData = React.useMemo(() => {
    if (!recentActivityAPIData?.timeline) return []

    let activityItems = recentActivityAPIData.timeline

    // Filter by assignee if toggle is on (similar to SharedHomeContext)
    if (assignedToMe && user?.username) {
      activityItems = activityItems.filter((item) => item.assignee?.username === user.username)
    }

    return transformRecentActivity(activityItems)
  }, [recentActivityAPIData, assignedToMe, user])

  // Calculate total amount due from real supplier data (for supplier overview)
  const totalSupplierPastDue = React.useMemo(
    () => supplierMetrics.reduce((sum, supplier) => sum + supplier.pastDueAmount, 0),
    [supplierMetrics]
  )

  // Calculate total amount due from top 10 oldest invoices (for invoice grid)
  const totalOldestInvoicesAmount = React.useMemo(
    () => top10OldestInvoices.reduce((sum, invoice) => sum + invoice.amountDue, 0),
    [top10OldestInvoices]
  )

  return (
    <>
      <Horizontal
        justifyContent='space-between'
        alignItems='center'
        bg='default'
        border='bottom'
        style={{ padding: 12 }}
      >
        <Horizontal gap={10} alignItems='center'>
          <Texto weight='bold' category='h4'>
            Launchpad
          </Texto>
          <BackgroundRefreshSpinner isRefreshing={isBackgroundRefreshing} />
        </Horizontal>
        <Horizontal alignItems='center' gap={6}>
          <Texto category='label' weight='bold'>
            ASSIGNED TO ME
          </Texto>
          <Switch size='small' checked={assignedToMe || false} onChange={setAssignedToMe} />
          <BBDTag style={{ width: 'min-content', marginLeft: '12px' }}>
            Past {trendsData?.length || 0} {(trendsData?.length || 0) === 1 ? 'Day' : 'Days'}
          </BBDTag>
        </Horizontal>
      </Horizontal>

      {hasError && (
        <Alert
          message='Failed to load some dashboard data'
          type='warning'
          showIcon
          closable
          style={{ margin: '16px 24px' }}
        />
      )}

      <Vertical
        bg='default'
        style={{
          borderRadius: 6,
          padding: 24,
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          height: 'calc(100vh - 120px)',
          overflow: 'visible',
        }}
        gap={10}
      >
        <Horizontal gap={12} style={{ overflow: 'visible' }}>
          {/* Auto-Approve Percentage Card */}
          <Vertical
            height='auto'
            style={{
              border: '1px solid #e0e0e0',
              borderRadius: 6,
              padding: 8,
              flex: 1,
              minWidth: 0,
              position: 'relative',
              overflow: 'visible',
            }}
          >
            <Texto style={{ flexShrink: 0, whiteSpace: 'nowrap' }}> Auto-Approve Percentage </Texto>
            {isLoading ? (
              <Horizontal
                gap={5}
                style={{ width: '100%', minWidth: 0 }}
                alignItems='center'
                justifyContent='space-between'
              >
                <Skeleton.Input active style={{ width: 120, height: 32 }} />
                <Skeleton.Input active style={{ width: 180, height: 70 }} />
              </Horizontal>
            ) : (
              <Horizontal
                gap={5}
                style={{
                  width: '100%',
                  minWidth: 0,
                  flexWrap: 'nowrap',
                  overflow: 'visible',
                }}
                alignItems='center'
                justifyContent='space-between'
              >
                <Vertical gap={2} alignItems='flex-start' style={{ flexShrink: 0 }}>
                  <Texto
                    category='h3'
                    weight='bold'
                    style={{
                      flexShrink: 0,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {metrics.autoApprovePercentage === 0 && hasValidTrendData
                      ? 'No data'
                      : `${(metrics.autoApprovePercentage * 100).toFixed(1)}%`}
                  </Texto>
                  <DeltaDisplay
                    value={hasValidTrendData && metrics.autoApprovePercentage > 0 ? trends.autoApprovePercentage : 0}
                    noDataMessage={
                      !hasValidTrendData || metrics.autoApprovePercentage === 0 ? 'No trend data' : undefined
                    }
                  />
                </Vertical>

                {/* Spark Chart */}
                <Horizontal
                  style={{ width: 180, height: 70, flexShrink: 0, alignItems: 'center', overflow: 'visible' }}
                >
                  <ResponsiveContainer width='100%' height='100%'>
                    <BarChart
                      data={autoApproveSparkData}
                      margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
                      barCategoryGap='5%'
                    >
                      <Tooltip
                        formatter={(value: number) => `${value.toFixed(1)}%`}
                        labelFormatter={(index: number) => autoApproveSparkData[index]?.date || ''}
                        labelStyle={{ color: '#666' }}
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e0e0e0',
                          borderRadius: '4px',
                          padding: '4px 8px',
                          zIndex: 9999,
                        }}
                        wrapperStyle={{
                          zIndex: 9999,
                        }}
                        wrapperStyle={{
                          zIndex: 9999,
                          marginTop: '50px',
                        }}
                        allowEscapeViewBox={{ x: true, y: true }}
                        offset={-110}
                      />
                      <Bar dataKey='value' fill='#76abf2' radius={[0, 0, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Horizontal>
              </Horizontal>
            )}
          </Vertical>

          {/* Total Past Due Card */}
          <Vertical
            height='auto'
            style={{
              border: '1px solid #e0e0e0',
              borderRadius: 6,
              padding: 8,
              flex: 1,
              minWidth: 0,
              position: 'relative',
              overflow: 'visible',
            }}
          >
            <Texto style={{ flexShrink: 0, whiteSpace: 'nowrap' }}> Total Past Due </Texto>
            {isLoading ? (
              <Horizontal
                gap={5}
                style={{ width: '100%', minWidth: 0 }}
                alignItems='center'
                justifyContent='space-between'
              >
                <Skeleton.Input active style={{ width: 120, height: 32 }} />
                <Skeleton.Input active style={{ width: 180, height: 70 }} />
              </Horizontal>
            ) : (
              <Horizontal
                gap={5}
                style={{
                  width: '100%',
                  minWidth: 0,
                  flexWrap: 'nowrap',
                  overflow: 'visible',
                }}
                alignItems='center'
                justifyContent='space-between'
              >
                <Vertical gap={2} alignItems='flex-start' style={{ flexShrink: 0 }}>
                  <Texto
                    category='h3'
                    weight='bold'
                    style={{
                      flexShrink: 0,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {formatDollarMetric(metrics.totalPastDue)}
                  </Texto>
                  <DeltaDisplay
                    value={hasValidTrendData ? trends.totalPastDue : 0}
                    invertColors
                    noDataMessage={!hasValidTrendData ? 'No trend data' : undefined}
                  />
                </Vertical>

                {/* Spark Chart */}
                <Horizontal
                  style={{ width: 180, height: 70, flexShrink: 0, alignItems: 'center', overflow: 'visible' }}
                >
                  <ResponsiveContainer width='100%' height='100%'>
                    <BarChart
                      data={pastDueSparkData}
                      margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
                      barCategoryGap='5%'
                    >
                      <Tooltip
                        formatter={(value: number) => formatDollarMetric(value)}
                        labelFormatter={(index: number) => pastDueSparkData[index]?.date || ''}
                        labelStyle={{ color: '#666' }}
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e0e0e0',
                          borderRadius: '4px',
                          padding: '4px 8px',
                          zIndex: 9999,
                        }}
                        wrapperStyle={{
                          zIndex: 9999,
                        }}
                        wrapperStyle={{
                          zIndex: 9999,
                          marginTop: '50px',
                        }}
                        allowEscapeViewBox={{ x: true, y: true }}
                        offset={-110}
                      />
                      <Bar dataKey='value' fill='#76abf2' radius={[0, 0, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Horizontal>
              </Horizontal>
            )}
          </Vertical>

          {/* Total Open Items Card */}
          <Vertical
            height='auto'
            style={{
              border: '1px solid #e0e0e0',
              borderRadius: 6,
              padding: 8,
              flex: 1,
              minWidth: 0,
              position: 'relative',
              overflow: 'visible',
            }}
          >
            <Texto style={{ flexShrink: 0, whiteSpace: 'nowrap' }}> Total Open Items </Texto>
            {isLoading ? (
              <Horizontal
                gap={5}
                style={{ width: '100%', minWidth: 0 }}
                alignItems='center'
                justifyContent='space-between'
              >
                <Skeleton.Input active style={{ width: 120, height: 32 }} />
                <Skeleton.Input active style={{ width: 180, height: 70 }} />
              </Horizontal>
            ) : (
              <Horizontal
                gap={5}
                style={{
                  width: '100%',
                  minWidth: 0,
                  flexWrap: 'nowrap',
                  overflow: 'visible',
                }}
                alignItems='center'
                justifyContent='space-between'
              >
                <Vertical gap={2} alignItems='flex-start' style={{ flexShrink: 0 }}>
                  <Texto
                    category='h3'
                    weight='bold'
                    style={{
                      flexShrink: 0,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {formatMetricNumber(metrics.totalOpenItems)}
                  </Texto>
                  <DeltaDisplay
                    value={hasValidTrendData ? trends.totalOpenItems : 0}
                    invertColors
                    noDataMessage={!hasValidTrendData ? 'No trend data' : undefined}
                  />
                </Vertical>

                {/* Spark Chart */}
                <Horizontal
                  style={{ width: 180, height: 70, flexShrink: 0, alignItems: 'center', overflow: 'visible' }}
                >
                  <ResponsiveContainer width='100%' height='100%'>
                    <BarChart
                      data={openItemsSparkData}
                      margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
                      barCategoryGap='5%'
                    >
                      <Tooltip
                        formatter={(value: number) => formatMetricNumber(value)}
                        labelFormatter={(index: number) => openItemsSparkData[index]?.date || ''}
                        labelStyle={{ color: '#666' }}
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e0e0e0',
                          borderRadius: '4px',
                          padding: '4px 8px',
                          zIndex: 9999,
                        }}
                        wrapperStyle={{
                          zIndex: 9999,
                        }}
                        wrapperStyle={{
                          zIndex: 9999,
                          marginTop: '50px',
                        }}
                        allowEscapeViewBox={{ x: true, y: true }}
                        offset={-110}
                      />
                      <Bar dataKey='value' fill='#76abf2' radius={[0, 0, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Horizontal>
              </Horizontal>
            )}
          </Vertical>

          {/* Total Unmatched Items Card */}
          <Vertical
            height='auto'
            style={{
              border: '1px solid #e0e0e0',
              borderRadius: 6,
              padding: 8,
              flex: 1,
              minWidth: 0,
              position: 'relative',
              overflow: 'visible',
            }}
          >
            <Texto style={{ flexShrink: 0, whiteSpace: 'nowrap' }}> Total Unmatched Items </Texto>
            {isLoading ? (
              <Horizontal
                gap={5}
                style={{ width: '100%', minWidth: 0 }}
                alignItems='center'
                justifyContent='space-between'
              >
                <Skeleton.Input active style={{ width: 120, height: 32 }} />
                <Skeleton.Input active style={{ width: 180, height: 70 }} />
              </Horizontal>
            ) : (
              <Horizontal
                gap={5}
                style={{
                  width: '100%',
                  minWidth: 0,
                  flexWrap: 'nowrap',
                  overflow: 'visible',
                }}
                alignItems='center'
                justifyContent='space-between'
              >
                <Vertical gap={2} alignItems='flex-start' style={{ flexShrink: 0 }}>
                  <Texto
                    category='h3'
                    weight='bold'
                    style={{
                      flexShrink: 0,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {formatMetricNumber(metrics.totalUnmatchedItems)}
                  </Texto>
                  <DeltaDisplay
                    value={hasValidTrendData ? trends.totalUnmatchedItems : 0}
                    invertColors
                    noDataMessage={!hasValidTrendData ? 'No trend data' : undefined}
                  />
                </Vertical>

                {/* Spark Chart */}
                <Horizontal
                  style={{ width: 180, height: 70, flexShrink: 0, alignItems: 'center', overflow: 'visible' }}
                >
                  <ResponsiveContainer width='100%' height='100%'>
                    <BarChart
                      data={unmatchedSparkData}
                      margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
                      barCategoryGap='5%'
                    >
                      <Tooltip
                        formatter={(value: number) => formatMetricNumber(value)}
                        labelFormatter={(index: number) => unmatchedSparkData[index]?.date || ''}
                        labelStyle={{ color: '#666' }}
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e0e0e0',
                          borderRadius: '4px',
                          padding: '4px 8px',
                          zIndex: 9999,
                        }}
                        wrapperStyle={{
                          zIndex: 9999,
                        }}
                        wrapperStyle={{
                          zIndex: 9999,
                          marginTop: '50px',
                        }}
                        allowEscapeViewBox={{ x: true, y: true }}
                        offset={-110}
                      />
                      <Bar dataKey='value' fill='#76abf2' radius={[0, 0, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Horizontal>
              </Horizontal>
            )}
          </Vertical>
        </Horizontal>
        <Horizontal gap={12} style={{ flex: 1, height: '100%', minHeight: 0, overflow: 'visible' }}>
          {/* Supplier Overview */}
          <Vertical
            style={{
              border: '1px solid #e0e0e0',
              borderRadius: 6,
              flex: 3.5,
              display: 'flex',
              flexDirection: 'column',
              background: 'var(--theme-background-default)',
              minHeight: '100%',
            }}
          >
            <Horizontal
              gap={5}
              bg='muted'
              style={{
                alignItems: 'center',
                borderBottom: '1px solid var(--theme-border-default)',
                padding: '2px 8px',
              }}
              className='px-3 py-2'
            >
              <Texto category='h5' weight='bold'>
                {' '}
                Supplier Overview{' '}
              </Texto>
            </Horizontal>

            <GraviGrid
              rowData={supplierMetrics}
              columnDefs={supplierColumnDefs}
              loading={isLoading}
              masterDetail={true}
              detailCellRenderer={TerminalDetailRenderer}
              agPropOverrides={{
                getRowId: (params: any) => params.data.supplierId,
                headerHeight: 40,
                rowHeight: 50,
                pagination: false,
                suppressPaginationPanel: true,
                suppressHorizontalScroll: false,
                suppressColumnVirtualisation: true,
                groupDisplayType: 'groupRows',
                rowGroupPanelShow: 'onlyWhenGrouping',
                suppressRowTransform: true,
                detailRowAutoHeight: true,
                detailCellRendererParams: {
                  style: { padding: '0 0 8px 0' },
                },
                context: {
                  assignedToMe,
                  dateRange,
                },
                domLayout: 'normal',
              }}
            />

            {/* Debug Terminal Data Display */}

            <Horizontal
              justifyContent='space-between'
              alignItems='center'
              border='top'
              bg='muted'
              style={{
                padding: '2px 8px',
                borderRadius: 6,
                minWidth: 0,
                flexShrink: 0,
              }}
            >
              <Texto weight='bold'>TOTAL PAST DUE</Texto>
              <Texto weight='bold' style={{ whiteSpace: 'nowrap', marginLeft: '8px' }}>
                ${totalSupplierPastDue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Texto>
            </Horizontal>
          </Vertical>

          {/* Invoice Overview */}
          <Vertical
            style={{
              border: '1px solid #e0e0e0',
              borderRadius: 6,
              flex: 3.5,
              display: 'flex',
              flexDirection: 'column',
              background: 'var(--theme-background-default)',
            }}
          >
            <Horizontal
              gap={5}
              bg='default'
              style={{
                alignItems: 'center',
                borderBottom: '1px solid var(--theme-border-default)',
                padding: '2px 8px',
              }}
              className='px-3 py-2'
            >
              <Texto category='h5' weight='bold'>
                {' '}
                Invoice Overview{' '}
              </Texto>
            </Horizontal>

            {/* Alert Metrics Row */}
            {isLoading ? (
              <Vertical flex='0' bg='default' style={{ padding: '12px 8px', overflow: 'visible' }}>
                <Horizontal style={{ gap: '40px' }}>
                  {Array.from({ length: 3 }).map((_, index) => (
                    <Vertical key={index} style={{ width: '33.33%' }}>
                      <Skeleton.Input active style={{ width: '100%', height: 120 }} />
                    </Vertical>
                  ))}
                </Horizontal>
              </Vertical>
            ) : (
              <Vertical flex='0' bg='default' style={{ padding: '12px 8px', overflow: 'visible' }}>
                <Horizontal style={{ gap: '40px' }}>
                  <StatusAggregation
                    title='Unmatched'
                    count={matchCounts.unmatched}
                    topSuppliers={supplierBreakdowns.unmatched.map((s) => ({ name: s.supplierName, count: s.count }))}
                    showTag={false}
                    showDot={true}
                    dotColor='#DC3545'
                    onHeaderClick={(event) => {
                      const url = createMatchViewUrl('/supplier_rec/match_view') + '&tab=unmatched'
                      handleNavigation(url, navigate, event)
                    }}
                    onSupplierClick={(supplierName, event) => {
                      const url =
                        createMatchViewUrl('/supplier_rec/match_view') +
                        `&tab=unmatched&supplier_name=${encodeURIComponent(supplierName)}`
                      handleNavigation(url, navigate, event)
                    }}
                  />

                  <StatusAggregation
                    title='Unapproved'
                    count={matchCounts.unapproved}
                    topSuppliers={supplierBreakdowns.unapproved.map((s) => ({ name: s.supplierName, count: s.count }))}
                    showTag={false}
                    showDot={true}
                    dotColor='#17A2B8'
                    onHeaderClick={(event) => {
                      const url = createMatchViewUrl('/supplier_rec/match_view') + '&tab=unapproved'
                      handleNavigation(url, navigate, event)
                    }}
                    onSupplierClick={(supplierName, event) => {
                      const url =
                        createMatchViewUrl('/supplier_rec/match_view') +
                        `&tab=unapproved&supplier_name=${encodeURIComponent(supplierName)}`
                      handleNavigation(url, navigate, event)
                    }}
                  />

                  <StatusAggregation
                    title='On Hold'
                    count={matchCounts.onHold}
                    topSuppliers={supplierBreakdowns.onHold.map((s) => ({ name: s.supplierName, count: s.count }))}
                    showTag={false}
                    showDot={true}
                    dotColor='#FFC107'
                    onHeaderClick={(event) => {
                      const url = createMatchViewUrl('/supplier_rec/match_view') + '&tab=on_hold'
                      handleNavigation(url, navigate, event)
                    }}
                    onSupplierClick={(supplierName, event) => {
                      const url =
                        createMatchViewUrl('/supplier_rec/match_view') +
                        `&tab=on_hold&supplier_name=${encodeURIComponent(supplierName)}`
                      handleNavigation(url, navigate, event)
                    }}
                  />
                </Horizontal>
              </Vertical>
            )}

            <Horizontal border='top' style={{ margin: '0px 0 0 0' }} />

            {/* Top 10 Oldest Invoices Header */}
            <Horizontal bg='default' style={{ padding: '2px 8px' }}>
              <Texto category='h5' weight='bold'>
                Top 10 Oldest Invoices
              </Texto>
            </Horizontal>

            <Vertical bg='default' style={{ flex: 1, position: 'relative' }}>
              <GraviGrid
                rowData={top10OldestInvoices}
                columnDefs={invoiceColumnDefs}
                loading={isLoading}
                storageKey={OLDEST_INVOICES_STORAGE_KEY}
                onGridConfigChanged={(config: any) =>
                  updateSetting(`gridConfig::${OLDEST_INVOICES_STORAGE_KEY}`, config)
                }
                {...gridViewManager(OLDEST_INVOICES_STORAGE_KEY, updateSetting)}
                agPropOverrides={{
                  getRowId: (params: any) => {
                    // Ensure we have a unique ID even if invoiceNumber is missing
                    const invoiceNum = params.data.invoiceNumber || 'unknown'
                    const matchId = params.data.matchId || params.data.supplierId || Math.random()
                    return `${matchId}-${invoiceNum}`
                  },
                  headerHeight: 40,
                  rowHeight: 35,
                  groupDisplayType: 'groupRows',
                  rowGroupPanelShow: 'onlyWhenGrouping',
                  context: {
                    matches,
                  },
                  rowClass: 'hover-link-container',
                  onRowClicked: (params: any) => {
                    const matchData = matches.find((match) => match.id === params.data.matchId)
                    if (matchData) {
                      const navigationResult = generateNavigationUrlForMatch(matchData, params.data.invoiceNumber)
                      handleGridRowClick(navigationResult.url, navigate, params)
                    } else {
                      handleGridRowClick('/supplier_rec/match_view', navigate, params)
                    }
                  },
                }}
              />
            </Vertical>
            <Horizontal
              justifyContent='space-between'
              alignItems='center'
              border='top'
              bg='muted'
              style={{
                padding: '2px 8px',
                borderRadius: 6,
                minHeight: 28,
                minWidth: 0,
                flexShrink: 0,
              }}
            >
              <Texto weight='bold'>TOTAL AMOUNT DUE</Texto>
              <Texto weight='bold' style={{ whiteSpace: 'nowrap', marginLeft: '8px' }}>
                $
                {totalOldestInvoicesAmount.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Texto>
            </Horizontal>
          </Vertical>

          {/* Recent Activity */}
          <Vertical
            style={{
              border: '1px solid #e0e0e0',
              borderRadius: 6,
              flex: 2,
              display: 'flex',
              flexDirection: 'column',
              background: 'var(--theme-background-default)',
              height: '100%', // Use full available height
            }}
          >
            <Horizontal
              gap={5}
              bg='muted'
              style={{
                alignItems: 'center',
                borderBottom: '1px solid var(--theme-border-default)',
                padding: '2px 8px',
              }}
              className='px-3 py-2'
            >
              <Texto category='h5' weight='bold'>
                {' '}
                Recent Activity{' '}
              </Texto>
            </Horizontal>
            <Vertical
              style={{
                flex: 1, // Take remaining space after header
                padding: '12px',
                overflowY: 'auto', // Auto scroll when needed
                overflowX: 'hidden',
                position: 'relative',
              }}
            >
              {isLoading ? (
                <Vertical style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {Array.from({ length: 5 }).map((_, index) => (
                    <Skeleton.Input key={index} active style={{ width: '100%', height: 120 }} />
                  ))}
                </Vertical>
              ) : (
                <Vertical scroll={true} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {recentActivityData.map((activity) => {
                    return (
                      <RecentActivityItem
                        key={activity.id}
                        invoiceId={activity.id}
                        displayId={activity.displayId}
                        actionType={activity.actionType}
                        description={activity.description}
                        personName={activity.personName}
                        timestamp={activity.timestamp}
                        onClick={(event) => {
                          if (activity.matchId) {
                            // Navigate directly to details page using the match_id from activity data
                            handleNavigation(`/supplier_rec/match_view/${activity.matchId}`, navigate, event)
                          } else {
                            // Fallback to general match view if no match_id
                            handleNavigation('/supplier_rec/match_view', navigate, event)
                          }
                        }}
                      />
                    )
                  })}
                </Vertical>
              )}
            </Vertical>
          </Vertical>
        </Horizontal>
      </Vertical>
    </>
  )
}

export default Launchpad
